import { Guide } from './guideTypes';

/* Facility Assessment user guide.

   Every score, band and rule below is taken directly from modules/facility/facilityCalculations.ts
   — the single source of the scoring engine, which in turn reproduces Paul Curd's Excel scorecard.
   When that engine changes, update the tables here (and the reference rows in particular) so the
   guide stays true. Do not hand-edit numbers to "round them off"; they are the exact points the app
   awards. */

export const facilityAssessmentGuide: Guide = {
  id: 'facility-assessment',
  title: 'Facility Assessment',
  subtitle: 'Scoring building systems and assets in MEP Tools',
  version: '1.1',
  updated: '2026-10-29',
  intro:
    'The Facility Assessment module turns a walk-through of a building into a defensible, numeric '
    + 'condition score. You record each piece of equipment (an "asset"), rate its condition against '
    + 'six criteria, and the app computes a 0–100 score and a plain-language rating for that asset, '
    + 'then rolls the results up by system and by building. This guide explains what to enter, how '
    + 'the score is calculated, and what the numbers on screen mean.',
  sections: [
    {
      id: 'overview',
      title: 'Overview',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'A facility assessment is organized into four building systems: **Controls**, '
            + '**Electrical**, **Mechanical**, and **Plumbing**. Within each system you add the '
            + 'individual assets you assessed — a rooftop unit, a switchgear line-up, a booster pump, '
            + 'and so on.',
        },
        {
          kind: 'bullets',
          items: [
            'Each **asset** is scored 0–100 from the six condition criteria described below, and '
            + 'given a rating (New → Failed).',
            'Each **system** score is the average of its assets’ scores. A system with no assets is '
            + 'left out of the building total rather than counted as a zero.',
            'The **building** score is the sum of the active systems’ scores, and carries its own '
            + 'rating band (see Condition Ratings).',
          ],
        },
      ],
    },
    {
      id: 'getting-started',
      title: 'Recording an Assessment',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Open the project and select the **Facility Assessment** tool.',
            'Create or open the assessment for the building you are surveying.',
            'Under the appropriate system (Controls, Electrical, Mechanical, or Plumbing), add an '
            + 'asset and give it a name and location.',
            'Enter the asset’s **age** and **remaining service life** in years, then select a rating '
            + 'for each of the four condition criteria.',
            'Attach photos and, where a floor plan is loaded, drop a plan pin to mark the asset’s '
            + 'location (see Photos & Floor-Plan Pins).',
            'Repeat for every assessed asset, then generate a report when the survey is complete '
            + '(see Generating Reports).',
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: 'Work offline in the field',
          text:
            'The module is built for on-site use with unreliable connectivity: entries and photos '
            + 'are captured locally and sync when you regain signal, so you can assess a whole '
            + 'building without a connection.',
        },
      ],
    },
    {
      id: 'scoring',
      title: 'How Asset Scoring Works',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'An asset’s score is normally the **sum of points from six criteria**, then clamped to '
            + 'the 0–100 range. Three criteria can only add points; three can add or subtract. The '
            + 'points each criterion can contribute are fixed by the scorecard and shown below.',
        },
        {
          kind: 'subheading',
          text: 'Equipment Age (max 20 points)',
        },
        {
          kind: 'paragraph',
          text:
            'Enter the true age in years. The app scores it in the nearest **5-year band** — the '
            + 'same bands the Excel dropdown offered — with ties going to the older (harsher) band, '
            + 'so an age of 2.5 years scores as the 5-year band, not the 0-year band.',
        },
        {
          kind: 'table',
          columns: ['Age band (years)', 'Points'],
          rows: [
            ['0', '20'],
            ['5', '15'],
            ['10', '10'],
            ['15', '5'],
            ['20', '2.5'],
            ['25', '0'],
            ['30 or more', '0'],
          ],
        },
        {
          kind: 'subheading',
          text: 'Remaining Service Life / RSL (max 20 points)',
        },
        {
          kind: 'paragraph',
          text:
            'The estimated years of useful life left. Scored in the same 5-year bands, but ties go '
            + 'to the shorter (harsher) band — 2.5 years left scores as the 0-year band. See '
            + 'Remaining Service Life for how RSL is derived.',
        },
        {
          kind: 'table',
          columns: ['RSL band (years)', 'Points'],
          rows: [
            ['0', '0'],
            ['5', '2.5'],
            ['10', '5'],
            ['15', '7.5'],
            ['20', '10'],
            ['25', '15'],
            ['30 or more', '20'],
          ],
        },
        {
          kind: 'subheading',
          text: 'Visual Inspection (−10 to +30 points)',
        },
        {
          kind: 'table',
          columns: ['Rating', 'Points'],
          rows: [
            ['New', '+30'],
            ['Like New', '+20'],
            ['Good', '+10'],
            ['Average', '+5'],
            ['Poor', '−5'],
            ['Critical', '−10'],
            ['Failed', '0'],
          ],
        },
        {
          kind: 'subheading',
          text: 'Operational Tests (−10 to +20 points)',
        },
        {
          kind: 'table',
          columns: ['Rating', 'Points'],
          rows: [
            ['Fully Functional', '+20'],
            ['Minor Issues', '−5'],
            ['Major Issues', '−10'],
            ['Failed', '0'],
          ],
        },
        {
          kind: 'subheading',
          text: 'Maintenance Records & Maintenance Service Level (max 5 points each)',
        },
        {
          kind: 'paragraph',
          text:
            'These two criteria are scored on the same scale and each contribute up to 5 points.',
        },
        {
          kind: 'table',
          columns: ['Rating', 'Points'],
          rows: [
            ['Above Average', '+5'],
            ['Average', '+2'],
            ['Below Average', '0'],
            ['None', '0'],
          ],
        },
        {
          kind: 'callout',
          tone: 'info',
          title: 'Points Awarded / Deducted',
          text:
            'On the asset form, each criterion shows the points it contributed, so you can '
            + 'sanity-check a score in the field. When a forced downgrade sets the score (next '
            + 'section), those per-criterion points are shown struck through, because they did not '
            + 'reach the total.',
        },
      ],
    },
    {
      id: 'downgrades',
      title: 'Forced Downgrades & the Strikethrough',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Some condition ratings are serious enough that they **override the additive score '
            + 'entirely**. When one applies, the asset’s score is pinned to a fixed low value and the '
            + 'six criteria are ignored — a failed or badly compromised unit cannot be rescued by a '
            + 'young age or good maintenance history. The scorecard calls this a forced downgrade.',
        },
        {
          kind: 'paragraph',
          text:
            'This is what the **strikethrough** on the asset form means. The individual criterion '
            + 'points are still displayed so you can see what the unit would otherwise have scored, '
            + 'but they are drawn with a line through them and greyed out to signal that they did not '
            + 'contribute to the final number. The score came from the forced downgrade instead.',
        },
        {
          kind: 'paragraph',
          text:
            'Downgrades are evaluated in the following order; the **first** condition that matches '
            + 'sets the score:',
        },
        {
          kind: 'table',
          columns: ['Condition selected', 'Score pinned to', 'Note shown'],
          rows: [
            ['Operational Tests = Failed, or Visual Inspection = Failed', '0', 'Failed Equipment'],
            ['Operational Tests = Major Issues', '7.5', 'Downgraded — Major Operational Issues'],
            ['Visual Inspection = Critical', '7.5', 'Downgraded — Visual Inspection Issues'],
            ['Visual Inspection = Poor', '12.5', 'Downgraded — Visual Inspection Issues'],
            ['Operational Tests = Minor Issues', '12.5', 'Downgraded — Minor Operational Issues'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warning',
          title: 'What the strikethrough tells you',
          text:
            'Struck-through points = overridden. The number you see beside a struck-through '
            + 'criterion is the contribution it *would* have made; the asset’s actual score is the '
            + 'pinned value from the matching row above. If no downgrade applies, nothing is struck '
            + 'through and the score is the plain sum of the six criteria.',
        },
      ],
    },
    {
      id: 'ratings',
      title: 'Condition Ratings',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'Scores are translated into a seven-level rating. Asset ratings and building ratings use '
            + 'different thresholds because they operate on different scales (an asset is 0–100; a '
            + 'building is the sum of its systems).',
        },
        {
          kind: 'subheading',
          text: 'Asset rating (from a 0–100 asset score)',
        },
        {
          kind: 'table',
          columns: ['Score', 'Rating', 'Meaning'],
          rows: [
            ['81–100', 'New', 'New, recently installed.'],
            ['66–80', 'Like New', 'No observed or reported issues, no failures expected.'],
            ['51–65', 'Good', 'No reported issues or concerns.'],
            ['26–50', 'Average', 'Minor reported issues with minor concerns.'],
            ['11–25', 'Poor', 'Minor repairs needed to continue operation.'],
            ['6–10', 'Critical', 'Major repairs needed to return to operation.'],
            ['0–5', 'Failed', 'Immediate replacement required.'],
          ],
        },
        {
          kind: 'subheading',
          text: 'Building rating (from the summed system scores)',
        },
        {
          kind: 'table',
          columns: ['Building score', 'Rating'],
          rows: [
            ['Over 320', 'New'],
            ['261–320', 'Like New'],
            ['201–260', 'Good'],
            ['101–200', 'Average'],
            ['41–100', 'Poor'],
            ['21–40', 'Critical'],
            ['0–20', 'Failed'],
          ],
        },
        {
          kind: 'callout',
          tone: 'info',
          title: 'Condition text overrides',
          text:
            'Two facts win over the rating-derived condition sentence on the asset card: if the '
            + 'remaining service life is 0, the card reads **Past Expected Lifetime**; if Operational '
            + 'Tests is Major Issues, it reads **Major Issues Observed**.',
        },
      ],
    },
    {
      id: 'rsl',
      title: 'Remaining Service Life (RSL)',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'RSL is one of the two heaviest age-related criteria and drives both the score and the '
            + '“Past Expected Lifetime” condition text. It is the estimated number of years of useful '
            + 'life the asset has left.',
        },
        {
          kind: 'paragraph',
          text:
            'Where the scorecard knows an equipment type’s expected lifetime, RSL can be derived from '
            + 'the asset’s age and description (the equipment type, chosen from the life-expectancy '
            + 'table). Many real-world asset types, however, have no '
            + 'expected lifetime on file — for those, **enter RSL manually** based on your field '
            + 'judgment. Review the RSL on every asset before finalizing; a blank or default RSL '
            + 'materially changes the score.',
        },
        {
          kind: 'callout',
          tone: 'warning',
          title: 'RSL of 0',
          text:
            'An RSL of 0 both scores zero points for that criterion and forces the condition text to '
            + '“Past Expected Lifetime.” Use it deliberately to flag equipment that is at or beyond '
            + 'the end of its service life.',
        },
      ],
    },
    {
      id: 'photos-plans',
      title: 'Photos & Floor-Plan Pins',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Attach one or more **photos** to each asset as condition evidence. Photos appear in the '
            + 'detailed report alongside the asset’s score.',
            'Upload a **floor plan** as an image or a **PDF**. A multi-page PDF drawing set is split '
            + 'into one selectable **sheet** per page, so you can pin against the actual construction '
            + 'drawings.',
            'Drop a **pin** on the plan to mark each asset’s location. Pins are colored by the '
            + 'asset’s rating, so you can read condition at a glance, and you can pinch or scroll to '
            + 'zoom into a drawing.',
          ],
        },
      ],
    },
    {
      id: 'filtering',
      title: 'Finding & Filtering Assets',
      blocks: [
        {
          kind: 'paragraph',
          text:
            'The system tabs, the Summary tab and the Plans tab share one set of filters. A filter you '
            + 'set follows you from tab to tab until you clear it.',
        },
        {
          kind: 'bullets',
          items: [
            '**Text filter** — type part of an asset’s ID, description or location.',
            '**Rating chips** — tap one or more ratings (Failed through New, plus **Not assessed**) to '
            + 'show only those assets. Each chip shows how many assets have that rating; tap it again to '
            + 'turn it off, or tap **Clear**.',
            '**Needs attention** — one tap selects Failed, Critical and Poor together: the same assets '
            + 'the Recommendations call out.',
            '**Show: No photo / No age** — on a system tab only, jumps to the assets still missing a '
            + 'photo or an age. This one resets when you change tabs.',
          ],
        },
        { kind: 'subheading', text: 'Across the building' },
        {
          kind: 'bullets',
          items: [
            'On the **Summary** tab, **Assets by rating, all systems** lists the matching assets from '
            + 'all four systems, worst first. Tap one to open it in its system tab.',
            'On the **Plans** tab, pins that don’t match the filter fade back so the matching ones stand '
            + 'out. Faded pins can still be tapped. If a filter would hide the asset you open from a pin, '
            + 'the filter is cleared.',
            'When a filter is hiding rows, the export button reads **Export N shown** and exports just '
            + 'those rows; the file name ends in “-filtered”. Reports always include every asset.',
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: 'A punch list after the walk',
          text:
            'On the Summary tab, tap **Needs attention** to list every Failed, Critical and Poor asset in '
            + 'the building, then switch to Plans to see where they are.',
        },
      ],
    },
    {
      id: 'reports',
      title: 'Generating Reports',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The module produces two branded, printable report tiers, both scoped to the assessment:',
        },
        {
          kind: 'bullets',
          items: [
            '**Summary report** — per-system scores and ratings with the full asset tables. The '
            + 'concise deliverable.',
            '**Detailed photo report** — a per-asset card with the condition breakdown and the '
            + 'photos captured for that asset. The evidence-backed deliverable.',
          ],
        },
        {
          kind: 'steps',
          items: [
            'From the assessment, open the report view.',
            'Choose **Summary** or **Detailed** and review it on screen.',
            'Use **Download PDF** for a branded PDF, or **Print** for a paper copy.',
          ],
        },
      ],
    },
  ],
};
